import { Cross2Icon } from '@radix-ui/react-icons';
import { Flex, TextField } from '@radix-ui/themes';
import {
  Country,
  DEFAULT_COUNTRY_INFO,
  formatNational,
  parseE164,
  toE164,
} from 'classes/helpers/phone.helper';
import { CountryPicker } from 'components/common/form/country-picker';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonTextHint } from 'components/common/form/text-hint';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import { t } from 'i18next';
import { IPhoneInput } from 'interfaces/forms/phone';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useState } from 'react';

// derive country + text from a stored E.164 value
const getDefaultValues = (
  value: string | undefined
): { country: Country; numberText: string } => {
  if (!value) {
    return { country: DEFAULT_COUNTRY_INFO, numberText: '' };
  }

  const parsed = parseE164(value);
  if (!parsed) {
    return { country: DEFAULT_COUNTRY_INFO, numberText: '' };
  }

  return { country: parsed.country, numberText: parsed.national };
};

export const CommonPhoneInput = (props: IPhoneInput) => {
  const defaultValues = getDefaultValues(props.defaultValue);

  const [country, setCountry] = useState(defaultValues.country);
  const [number, setNumber] = useState(defaultValues.numberText);

  const handleChange = ({
    number,
    country,
  }: {
    number: string;
    country: Country;
  }) => {
    const e164 = toE164(number, country.code);
    props.onChange(number.trim() === '' ? '' : e164);
  };

  const onNumberChange = (value: string) => {
    setNumber(value);
    handleChange({ number: value, country });
  };

  const onCountryChange = (value: Country) => {
    setCountry(value);
    handleChange({ number, country: value });
  };

  // blank is allowed but non-empty unparseable text is invalid
  const invalid = number.trim() !== '' && toE164(number, country.code) === null;

  const color =
    props.showError && invalid ? RADIX.COLOR.DANGER : props.inputColor;

  return (
    <CommonInputWrapper {...props}>
      <CommonInputLabel {...props} />

      <Flex align="center" gap="2">
        <CountryPicker
          country={country}
          disabled={props.disabled}
          onChange={onCountryChange}
        />

        <TextField.Root
          size={props.size}
          // radix does not display the color until the input is focused, so we force it here
          style={{
            flex: 1,
            ...(color
              ? {
                  outlineOffset: '-1px',
                  outline: '2px solid var(--text-field-focus-color)',
                }
              : undefined),
          }}
          color={color}
          inputMode="tel"
          disabled={props.disabled}
          placeholder={
            props.placeholder ? t(props.placeholder).toString() : undefined
          }
          value={number}
          onChange={(e) => onNumberChange(e.target.value)}
          // keep the raw text when it has no digits to format, so invalid input stays visible
          onBlur={() =>
            setNumber(formatNational(number, country.code) || number)
          }
        >
          {!props.disabled && number.trim() !== '' && (
            <TextField.Slot
              side="right"
              className="cursor-pointer"
              onClick={() => onNumberChange('')}
            >
              <Cross2Icon />
            </TextField.Slot>
          )}
        </TextField.Root>
      </Flex>

      <CommonTextHint hint_md={props.hint_md} />
    </CommonInputWrapper>
  );
};
